<template>
  <span class="inline-date-sections">
    <inline-descriptor
      ref="descriptorRef"
      :id="id"
      :model-value="computedModel"
      :schema="computedSchema"
      :separators="descriptorSeparators"
      :update-token="tokenLimiter"
      :editable="editable"
      :is-table="false"
      :enable-menu="enableMenu"
      :move-to-body="moveToBody"
      :menu-z-index="menuZIndex"
      :menu-fit-content="true"
      @update:modelValue="onUpdateModelValue"
      @update-menu="onUpdateMenu"
      @keydown.up.exact.prevent="onArrowUp"
      @keydown.down.exact.prevent="onArrowDown"
      @keydown.enter.exact.prevent="onEnter"
    >
      <template #menu-content>
        <slot name="menu-content"></slot>
      </template>
    </inline-descriptor>
  </span>
</template>

<script lang="ts">
import _ from 'lodash';
import { reactive } from 'vue';
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import InlineDescriptor, { SchemaField, Token } from '@/components/multiedit/temporary/InlineDescriptor.vue';

export type DateSectionKey = 'day' | 'month' | 'year' | 'hour' | 'minute';

export interface DateSectionDef {
  key: DateSectionKey;
  length: number;
}

// eventKind comes from the descriptor, where 0 is input, 1 is leaving the field and 2 is
// a click or a change of section
export interface DateSectionsMenuEvent {
  key: string | null;
  text: string;
  eventKind: number;
}

export interface DateSectionsStepEvent {
  key: string;
  delta: number;
}

/**
 * Group of digit sections. One InlineDescriptor over a passed set of sections with a
 * single separator between them. The set and the separator come as props, so the group
 * knows nothing about dates and about time, it keeps digits and tells the owner what
 * the user does. A separator for the whole group is what keeps the two places that draw
 * it in the descriptor from disagreeing, and the inline mode is forced here because a
 * frame and a label belong to the whole field and not to a single section.
 */
@Component({
  name: 'InlineDateSections',
  components: { InlineDescriptor },
  emits: ['update:values', 'menu', 'step', 'enter'],
  expose: [
    'activateSection',
    'openMenu',
    'hideMenu',
    'applyModel',
    'leaveActiveField',
  ],
})
export default class InlineDateSections extends Vue {
  @Prop({ type: String, required: true }) id!: string;
  @Prop({ type: Array, required: true }) sections!: DateSectionDef[];
  // One separator for the whole group. Two sources write it in the descriptor, and they
  // agree only while every field of a descriptor carries the same sign
  @Prop({ type: String, default: '.' }) separator!: string;
  @Prop({ type: Object, required: true }) values!: Record<string, string>;
  @Prop({ type: Boolean, default: true }) editable!: boolean;
  @Prop({ type: Boolean, default: false }) enableMenu!: boolean;
  @Prop({ type: Boolean, default: false }) moveToBody!: boolean;
  @Prop({ type: Number, default: 0 }) menuZIndex!: number;

  computedModel: Record<string, Token[]> = reactive({});
  activeKey: string | null = null;

  // Values the group has already decided on. The prop comes back a tick later, and two
  // keystrokes can land inside one tick, so the owner of the values here is the group
  private localValues: Record<string, string> = {};

  created() {
    this.localValues = this.currentValues();
    this.computedModel = reactive(this.createModel());
  }

  get computedSchema(): SchemaField[] {
    return this.sections.map((section) => ({
      key: section.key,
      title: '',
      type: 'string',
      separator: this.separator,
      inputPattern: /[0-9]/,
      placeholder: this.$t(`date_ph_${section.key}`),
    }));
  }

  // The descriptor takes the sign for an empty section from this array
  get descriptorSeparators(): string[] {
    return [this.separator];
  }

  // Length of a section is a property of the section itself, so the markup is cut by the
  // same hook with which the descriptor checks a token against the type of the field. It
  // runs in normalizeSection on every input and before the next keystroke, so a section
  // never holds more digits than it has places
  get tokenLimiter(): (token: Token, field: SchemaField) => Token {
    const limits = new Map(this.sections.map((section) => [section.key, section.length]));
    return (token: Token, field: SchemaField): Token => {
      const limit = limits.get(field.key as DateSectionKey);
      if (!limit) {
        return token;
      }
      const digits = (token.text || '').replace(/\D+/g, '');
      return { ...token, text: digits.slice(0, limit) };
    };
  }

  // The prop repeats what the group has just emitted, and rebuilding the model on that
  // echo would drop the caret, so only a value that came from elsewhere is taken
  @Watch('values', { deep: true })
  onValuesChanged(val: Record<string, string>) {
    if (_.isEqual(val, this.localValues)) {
      return;
    }
    this.localValues = { ...val };
    this.computedModel = reactive(this.createModel());
    // The markup is drawn by the field itself. A value that matches the model the
    // descriptor already has would never reach the section, because its watcher
    // compares models and stops before the write
    this.$nextTick(() => this.applyModel());
  }

  onUpdateModelValue(newModel: Record<string, Token[]>) {
    const next: Record<string, string> = {};
    this.sections.forEach((section) => {
      const tokens = newModel[section.key];
      next[section.key] = Array.isArray(tokens)
        ? tokens.map((token) => token.text).join('')
        : this.localValues[section.key] || '';
    });
    this.localValues = { ...next };
    this.sections.forEach((section) => this.writeModel(section.key, next[section.key]));
    this.$emit('update:values', next);
  }

  onUpdateMenu(value: { fieldKey: string | null; sectionText: string; eventKind: number }) {
    const key = value.fieldKey;
    let text = value.sectionText || '';

    if (value.eventKind === 1) {
      this.activeKey = null;
    } else if (key) {
      this.activeKey = key;
      if (value.eventKind === 0) {
        text = this.applyInput(key, text);
      }
    }

    this.$emit('menu', {
      key,
      text,
      eventKind: value.eventKind,
    } as DateSectionsMenuEvent);
  }

  // Value of a section is read from the markup, which the limiter has already cut to the
  // places of the section. The model is not written here on purpose: while the caret is
  // in a section its visible text belongs to the markup, and a model write would make
  // the descriptor redraw the section under the caret. It catches up when the descriptor
  // saves the section itself, on a switch and on leaving
  private applyInput(key: string, text: string): string {
    const section = this.sections.find((item) => item.key === key);
    const current = this.localValues[key] || '';
    if (!section) {
      return current;
    }
    const next = text.replace(/\D+/g, '').slice(0, section.length);
    if (next === current) {
      return current;
    }
    this.localValues = { ...this.localValues, [key]: next };
    this.$emit('update:values', { ...this.localValues });
    return next;
  }

  private writeModel(key: string, text: string) {
    this.computedModel[key] = text
      ? [{ text, type: 'string', clickable: false }]
      : [];
  }

  onArrowUp() {
    this.emitStep(1);
  }

  onArrowDown() {
    this.emitStep(-1);
  }

  onEnter() {
    this.$emit('enter');
  }

  private emitStep(delta: number) {
    if (!this.activeKey) {
      return;
    }
    this.$emit('step', { key: this.activeKey, delta } as DateSectionsStepEvent);
  }

  activateSection(key: string) {
    const descriptor = this.$refs.descriptorRef as any;
    descriptor?.activateSectionByKey?.(key);
  }

  openMenu(anchor: HTMLElement | null) {
    const descriptor = this.$refs.descriptorRef as any;
    descriptor?.openMenu?.('container', anchor);
  }

  hideMenu() {
    const descriptor = this.$refs.descriptorRef as any;
    descriptor?.hideMenu?.();
  }

  applyModel() {
    const descriptor = this.$refs.descriptorRef as any;
    descriptor?.applyModel?.();
  }

  leaveActiveField() {
    const descriptor = this.$refs.descriptorRef as any;
    descriptor?.leaveActiveField?.();
  }

  private currentValues(): Record<string, string> {
    const values: Record<string, string> = {};
    this.sections.forEach((section) => {
      values[section.key] = this.values[section.key] || '';
    });
    return values;
  }

  private createModel(): Record<string, Token[]> {
    const model: Record<string, Token[]> = {};
    this.sections.forEach((section) => {
      const text = this.values[section.key] || '';
      model[section.key] = text ? [{ text, type: 'string', clickable: false }] : [];
    });
    return model;
  }
}
</script>

<style scoped lang="scss">
.inline-date-sections {
  display: inline;
}

.inline-date-sections :deep(.inline-descriptor) {
  white-space: nowrap;
}

.inline-date-sections :deep(.token) {
  vertical-align: baseline;
}

.inline-date-sections :deep(.section:not(:last-child) .token:empty)::after {
  content: attr(data-separator);
}
</style>
